import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Paperclip, Mic, StopCircle, Plus, Settings, Trash2, MessageSquare, Upload, X, Home, ChevronLeft, ChevronRight, Brain, Copy, Check, ArrowDown, Pin, Square, Pencil } from 'lucide-react';
import ErnieProBanner from '@/components/ErnieProBanner';
import ErnieProUpgradeModal from '@/components/ErnieProUpgradeModal';
import ErnieProBadge from '@/components/ErnieProBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AI_MODELS = [
  { value: 'gpt-5', label: 'GPT-5 (OpenAI) - Default', provider: 'OpenAI' },
  { value: 'gpt-4o', label: 'GPT-4o (OpenAI)', provider: 'OpenAI' },
  { value: 'claude-sonnet-4', label: 'Claude Sonnet 4 (Anthropic)', provider: 'Anthropic' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Google)', provider: 'Google' },
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Google)', provider: 'Google' }
];

export default function ErnieAiPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [proStatus, setProStatus] = useState(null);
  const [proSettings, setProSettings] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-5');  // Default to GPT-5
  const [testQuestionMode, setTestQuestionMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAdminControls, setShowAdminControls] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const editInputRef = useRef(null);
  
  // Copy message content to clipboard
  const copyToClipboard = async (content, messageId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      toast.success('Copied to clipboard');
      // Reset after 2 seconds
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  // Stop AI generation
  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
      toast.info('Stopped generating');
    }
  };
  
  // Start editing a conversation title
  const startEditingTitle = (conv) => {
    setEditingConversationId(conv.id);
    setEditingTitle(conv.title || 'New Conversation');
    // Focus the input after render
    setTimeout(() => editInputRef.current?.focus(), 50);
  };
  
  // Save edited conversation title
  const saveConversationTitle = async (conversationId) => {
    const trimmedTitle = editingTitle.trim();
    if (!trimmedTitle) {
      setEditingConversationId(null);
      return;
    }
    
    try {
      await axios.put(
        `${API}/ernie-ai/conversations/${conversationId}`,
        { title: trimmedTitle },
        { withCredentials: true }
      );
      // Update local state
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, title: trimmedTitle } : conv
      ));
      toast.success('Conversation renamed');
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      toast.error('Failed to rename conversation');
    }
    
    setEditingConversationId(null);
    setEditingTitle('');
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };
  
  // Toggle pin status for a conversation
  const togglePin = async (conversationId, currentPinned) => {
    try {
      await axios.put(
        `${API}/ernie-ai/conversations/${conversationId}`,
        { pinned: !currentPinned },
        { withCredentials: true }
      );
      // Update local state
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, pinned: !currentPinned } : conv
      ));
      toast.success(currentPinned ? 'Unpinned conversation' : 'Pinned conversation');
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update pin status');
    }
  };
  
  // Check if user is near bottom of messages
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    }
  };
  
  // Scroll to bottom function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Fetch Pro status and settings
  useEffect(() => {
    const fetchProStatus = async () => {
      try {
        const [statusRes, settingsRes] = await Promise.all([
          axios.get(`${API}/ernie-pro/status`, { withCredentials: true }),
          axios.get(`${API}/ernie-pro/settings`)
        ]);
        console.log('Pro Status Response:', statusRes.data);
        console.log('Pro Settings Response:', settingsRes.data);
        setProStatus(statusRes.data);
        setProSettings(settingsRes.data);
      } catch (error) {
        console.error('Failed to fetch Pro status:', error);
        console.log('Using fallback: Free user status');
        // Fallback to free user status if fetch fails
        setProStatus({ status: 'free', has_pro: false });
        setProSettings({ monthly_price: 5.99, is_free: false });
      }
    };
    fetchProStatus();
  }, []);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/users/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    loadConversations();
    loadDefaultModel();
  }, []);

  const loadDefaultModel = async () => {
    try {
      const response = await axios.get(`${API}/page-settings`);
      const ernieAiPage = response.data.find(p => p.page_name === 'ernie_ai');
      if (ernieAiPage?.default_ai_model) {
        setSelectedModel(ernieAiPage.default_ai_model);
      }
    } catch (error) {
      console.error('Failed to load default AI model:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/ernie-ai/conversations`, {
        withCredentials: true
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const createNewConversation = async () => {
    try {
      const response = await axios.post(`${API}/ernie-ai/conversations`, {}, {
        withCredentials: true
      });
      setConversations([response.data, ...conversations]);
      setCurrentConversation(response.data);
      setMessages([]);
      setUploadedFiles([]);
      toast.success('New conversation started');
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };
  
  const generateConversationTitle = (firstMessage) => {
    // Generate a short title from the first message
    if (!firstMessage) return 'New Conversation';
    const words = firstMessage.split(' ').slice(0, 5).join(' ');
    return words.length > 40 ? words.substring(0, 40) + '...' : words;
  };
  
  const updateConversationTitle = async (conversationId, firstMessage) => {
    const title = generateConversationTitle(firstMessage);
    try {
      await axios.put(
        `${API}/ernie-ai/conversations/${conversationId}`,
        { title },
        { withCredentials: true }
      );
      loadConversations();
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/ernie-ai/conversations/${conversationId}`, {
        withCredentials: true
      });
      setCurrentConversation(response.data);
      setMessages(response.data.messages || []);
      setSelectedModel(response.data.ai_model);
      setTestQuestionMode(response.data.test_question_mode);
      setUploadedFiles([]);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const updateConversationSettings = async (updates) => {
    if (!currentConversation) return;
    
    try {
      await axios.put(
        `${API}/ernie-ai/conversations/${currentConversation.id}`,
        updates,
        { withCredentials: true }
      );
      setCurrentConversation({ ...currentConversation, ...updates });
      
      // Update in list
      setConversations(conversations.map(conv => 
        conv.id === currentConversation.id ? { ...conv, ...updates } : conv
      ));
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Delete this conversation?')) return;
    
    try {
      await axios.delete(`${API}/ernie-ai/conversations/${conversationId}`, {
        withCredentials: true
      });
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };
  
  const clearAllConversations = async () => {
    if (!window.confirm('Delete ALL conversations? This cannot be undone!')) return;
    
    try {
      // Delete all conversations
      for (const conv of conversations) {
        await axios.delete(`${API}/ernie-ai/conversations/${conv.id}`, {
          withCredentials: true
        });
      }
      
      setConversations([]);
      setCurrentConversation(null);
      setMessages([]);
      toast.success('All conversations deleted');
    } catch (error) {
      toast.error('Failed to delete conversations');
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/ernie-ai/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setUploadedFiles([...uploadedFiles, {
          url: response.data.file_url,
          name: response.data.filename
        }]);
        
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setUploading(true);
          try {
            const formData = new FormData();
            formData.append('file', file, 'pasted-image.png');
            
            const response = await axios.post(`${API}/ernie-ai/upload`, formData, {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setUploadedFiles([...uploadedFiles, {
              url: response.data.file_url,
              name: 'Pasted Image'
            }]);
            
            toast.success('Image pasted successfully');
          } catch (error) {
            toast.error('Failed to upload pasted image');
          }
          setUploading(false);
        }
      }
    }
  };

  const sendMessage = async () => {
    if (!currentConversation) {
      await createNewConversation();
      return;
    }

    if (!inputText.trim() && uploadedFiles.length === 0) return;

    const isFirstMessage = messages.length === 0;
    const userMessage = inputText;
    
    setLoading(true);
    
    // Create abort controller for this request
    abortControllerRef.current = new AbortController();
    
    try {
      const response = await axios.post(
        `${API}/ernie-ai/conversations/${currentConversation.id}/messages`,
        {
          text: inputText,
          file_urls: uploadedFiles.map(f => f.url)
        },
        { 
          withCredentials: true,
          signal: abortControllerRef.current.signal
        }
      );
      
      // Add user message and AI response
      setMessages([
        ...messages,
        {
          role: 'user',
          content: inputText,
          file_urls: uploadedFiles.map(f => f.url),
          created_at: new Date().toISOString()
        },
        response.data
      ]);
      
      // Auto-generate title from first message
      if (isFirstMessage && userMessage) {
        updateConversationTitle(currentConversation.id, userMessage);
      }
      
      setInputText('');
      setUploadedFiles([]);
    } catch (error) {
      // Check if request was aborted
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // Request was stopped by user - add user message but no AI response
        setMessages([
          ...messages,
          {
            role: 'user',
            content: inputText,
            file_urls: uploadedFiles.map(f => f.url),
            created_at: new Date().toISOString()
          }
        ]);
        setInputText('');
        setUploadedFiles([]);
        return;
      }
      
      console.error('Send message error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send message';
      
      // Handle specific error types
      if (errorMsg.includes('502') || errorMsg.includes('Bad Gateway')) {
        toast.error('⚠️ AI service temporarily unavailable. Please try again in a moment.');
      } else if (errorMsg.includes('timeout')) {
        toast.error('⏱️ Request timed out. Please try a shorter message or try again.');
      } else if (errorMsg.includes('rate limit')) {
        toast.error('⏸️ Too many requests. Please wait a moment before trying again.');
      } else {
        toast.error(errorMsg);
      }
      
      // Remove the user message that was optimistically added
      setMessages(messages);
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#2C1810]">
      {/* Top Navigation Bar */}
      <div className="bg-[#3D2817] border-b-4 border-[#E85D4D] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left: Home Button */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#F4E4C1] hover:text-[#E8934D] transition-colors font-bold"
          >
            <Home className="w-5 h-5" />
            <span>Home</span>
          </Link>
          
          {/* Center: Ernie AI Logo & Badge */}
          <div className="flex items-center gap-3">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl retro-title text-[#F4E4C1] mb-1">🤖 Ernie.AI</h1>
              <p className="text-xs text-[#E8B67A]">
                {proStatus?.has_pro ? 'Your Unlimited AI Assistant' : 'TCM Healthcare Assistant'}
              </p>
            </div>
            {proStatus?.has_pro && <ErnieProBadge />}
          </div>
          
          {/* Right: Spacer for balance */}
          <div className="w-24"></div>
        </div>
      </div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Toggle Button (when collapsed) */}
        {!showSidebar && (
          <button
            onClick={() => setShowSidebar(true)}
            className="absolute left-0 top-1/2 z-10 bg-[#E8934D] text-white p-2 rounded-r-lg hover:bg-[#E85D4D] transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
        
        {/* Sidebar */}
        {showSidebar && (
          <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} bg-[#3D2817] border-r-4 sunset-border-right overflow-y-auto transition-all duration-300`}>
            <div className="p-4">
              {!sidebarCollapsed && (
                <>
                  <button
                    onClick={createNewConversation}
                    className="retro-button w-full flex items-center justify-center gap-2 mb-4"
                  >
                    <Plus className="w-5 h-5" />
                    New Conversation
                  </button>
                  
                  {/* Memory Button (Pro Only) */}
                  {proStatus?.has_pro && (
                    <button
                      onClick={() => navigate('/ernie-ai/memories')}
                      className="retro-button w-full flex items-center justify-center gap-2 mb-4 bg-purple-600 hover:bg-purple-700"
                    >
                      <Brain className="w-5 h-5" />
                      My Memories
                    </button>
                  )}
                  
                  {/* Clear All Button */}
                  {conversations.length > 0 && (
                    <button
                      onClick={clearAllConversations}
                      className="retro-button w-full flex items-center justify-center gap-2 mb-4 bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                      Clear All
                    </button>
                  )}

                  <div className="space-y-2">
                    {/* Sort conversations: pinned first, then by date */}
                    {[...conversations]
                      .sort((a, b) => {
                        // Pinned conversations first
                        if (a.pinned && !b.pinned) return -1;
                        if (!a.pinned && b.pinned) return 1;
                        // Then by date (newest first)
                        return new Date(b.created_at) - new Date(a.created_at);
                      })
                      .map(conv => {
                      const createdDate = new Date(conv.created_at);
                      const dateStr = createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      
                      return (
                        <div
                          key={conv.id}
                          className={`p-3 rounded cursor-pointer ${
                            currentConversation?.id === conv.id
                              ? 'bg-[#E8934D]'
                              : conv.pinned 
                                ? 'bg-[#7B5423] hover:bg-[#8B5423] border-l-4 border-yellow-500'
                                : 'bg-[#6B4423] hover:bg-[#8B5423]'
                          } transition-colors`}
                        >
                          <div className="flex items-center justify-between">
                            {editingConversationId === conv.id ? (
                              // Editing mode
                              <div className="flex-1 flex items-center gap-2">
                                <input
                                  ref={editInputRef}
                                  type="text"
                                  value={editingTitle}
                                  onChange={(e) => setEditingTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      saveConversationTitle(conv.id);
                                    } else if (e.key === 'Escape') {
                                      cancelEditing();
                                    }
                                  }}
                                  onBlur={() => saveConversationTitle(conv.id)}
                                  className="flex-1 px-2 py-1 bg-[#2C1810] text-[#F4E4C1] text-sm rounded border border-[#E8934D] focus:outline-none focus:border-[#E85D4D]"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`edit-title-input-${conv.id}`}
                                />
                              </div>
                            ) : (
                              // Normal display mode
                              <button
                                onClick={() => loadConversation(conv.id)}
                                className="flex-1 text-left flex flex-col gap-1 min-w-0"
                              >
                                <div className="flex items-center gap-2">
                                  {conv.pinned && <Pin className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />}
                                  <MessageSquare className="w-4 h-4 text-[#F4E4C1] flex-shrink-0" />
                                  <span className="text-[#F4E4C1] font-bold text-sm truncate">
                                    {conv.title || 'New Conversation'}
                                  </span>
                                </div>
                                <span className="text-xs text-[#E8B67A] ml-6">{dateStr}</span>
                              </button>
                            )}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingTitle(conv);
                                }}
                                className="p-1 text-[#F4E4C1] hover:bg-[#8B5423] rounded transition-colors"
                                title="Rename conversation"
                                data-testid={`rename-conv-${conv.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePin(conv.id, conv.pinned);
                                }}
                                className={`p-1 rounded transition-colors ${
                                  conv.pinned 
                                    ? 'text-yellow-400 hover:bg-yellow-500/20' 
                                    : 'text-[#F4E4C1] hover:bg-[#8B5423]'
                                }`}
                                title={conv.pinned ? 'Unpin' : 'Pin conversation'}
                                data-testid={`pin-conv-${conv.id}`}
                              >
                                <Pin className={`w-4 h-4 ${conv.pinned ? 'fill-yellow-400' : ''}`} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteConversation(conv.id);
                                }}
                                className="p-1 hover:bg-[#E85D4D] rounded transition-colors"
                                title="Delete conversation"
                              >
                                <Trash2 className="w-4 h-4 text-[#F4E4C1]" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              
              {/* Collapse Button */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="retro-button w-full flex items-center justify-center gap-2 mt-4"
                title={sidebarCollapsed ? 'Expand' : 'Collapse'}
              >
                {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
              </button>
            </div>
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Admin Controls - Collapsible */}
          {user?.role === 'admin' && currentConversation && (
            <div className="bg-[#3D2817] border-b-2 border-[#8B6F47]">
              <button
                onClick={() => setShowAdminControls(!showAdminControls)}
                className="w-full p-2 text-[#F4E4C1] hover:bg-[#4D3827] transition-colors text-sm font-bold"
              >
                {showAdminControls ? '▼' : '▶'} Admin Controls
              </button>
              
              {showAdminControls && (
                <div className="p-4 flex items-center gap-4">
                  <select
                    value={selectedModel}
                    onChange={(e) => {
                      setSelectedModel(e.target.value);
                      updateConversationSettings({ ai_model: e.target.value });
                    }}
                    className="px-3 py-1 bg-[#6B4423] text-[#F4E4C1] rounded text-sm border-2 border-[#E8934D]"
                  >
                    {AI_MODELS.map(model => (
                      <option key={model.value} value={model.value}>{model.label}</option>
                    ))}
                  </select>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={testQuestionMode}
                      onChange={(e) => {
                        setTestQuestionMode(e.target.checked);
                        updateConversationSettings({ test_question_mode: e.target.checked });
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-[#F4E4C1] text-sm">Test Question Mode</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <div 
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-[#2C1810] to-[#1C1008] relative"
          >
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Pro Upgrade Banner for Free Users */}
              {proStatus && !proStatus.has_pro && (
                <ErnieProBanner 
                  onUpgradeClick={() => setShowUpgradeModal(true)} 
                  monthlyPrice={proSettings?.monthly_price || 5.99}
                />
              )}
              
              {/* Messages */}
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-2xl p-4 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-[#E8934D] text-white'
                        : 'cassette-card'
                    }`}
                  >
                    {msg.file_urls && msg.file_urls.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {msg.file_urls.map((url, i) => (
                          <div key={i} className="text-xs opacity-75">
                            📎 {url.split('/').pop()}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className={`whitespace-pre-wrap ${msg.role === 'user' ? 'text-white' : 'text-[#2C1810]'}`}>
                      {msg.content}
                    </div>
                    
                    {/* Copy Button for AI responses */}
                    {msg.role === 'assistant' && !msg.is_upgrade_prompt && (
                      <div className="mt-3 pt-2 border-t border-[#E8934D]/20">
                        <button
                          onClick={() => copyToClipboard(msg.content, msg.id || index)}
                          className="flex items-center gap-1.5 text-xs text-[#8B6F47] hover:text-[#E8934D] transition-colors"
                          data-testid={`copy-message-${index}`}
                          title="Copy to clipboard"
                        >
                          {copiedMessageId === (msg.id || index) ? (
                            <>
                              <Check className="w-4 h-4" />
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* Upgrade button for Pro prompt messages */}
                    {msg.is_upgrade_prompt && (
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="mt-4 px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-400 text-yellow-900 font-bold rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                      >
                        ⭐ Upgrade to Pro Now
                      </button>
                    )}
                    
                    {/* Admin-Only Model Indicator */}
                    {msg.role === 'assistant' && user?.role === 'admin' && (msg.model_used || msg.provider_used) && (
                      <div className="mt-2 pt-2 border-t border-[#E8934D]/30">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-[#E8934D]">
                            🤖 Model:
                          </span>
                          <span className="text-xs text-[#6B4423] bg-[#FFF8E7] px-2 py-0.5 rounded">
                            {msg.provider_used === 'openai' && msg.model_used === 'gpt-5' && '✨ GPT-5'}
                            {msg.provider_used === 'openai' && msg.model_used === 'gpt-4o' && 'GPT-4o'}
                            {msg.provider_used === 'anthropic' && 'Claude Sonnet 4'}
                            {msg.provider_used === 'gemini' && msg.model_used === 'gemini-2.5-pro' && '🖼️ Gemini 2.5 Pro (Image Mode)'}
                            {msg.provider_used === 'gemini' && msg.model_used === 'gemini-2.0-flash' && 'Gemini 2.0 Flash'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {msg.confidence_score !== null && msg.confidence_score !== undefined && (
                      <div className="mt-3 pt-3 border-t-2 border-[#E8934D]">
                        <div className="text-sm font-bold text-[#E85D4D]">
                          Confidence: {msg.confidence_score}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading Indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="cassette-card p-4 max-w-2xl">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-[#E8934D] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-3 h-3 bg-[#E85D4D] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-3 h-3 bg-[#E8934D] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className="text-[#6B4423] text-sm font-medium">Ernie is thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
            
            {/* Scroll to Bottom Button */}
            {showScrollButton && (
              <button
                onClick={scrollToBottom}
                className="fixed bottom-32 right-8 z-20 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-all hover:scale-110 border border-gray-200"
                data-testid="scroll-to-bottom-btn"
                title="Scroll to bottom"
              >
                <ArrowDown className="w-5 h-5 text-gray-600" />
              </button>
            )}
          </div>

          {/* Input Area */}
          <div className="bg-[#3D2817] border-t-4 sunset-border-top p-4">
            <div className="max-w-4xl mx-auto">
              {uploadedFiles.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="bg-[#6B4423] text-[#F4E4C1] px-3 py-1 rounded flex items-center gap-2 text-sm">
                      <span>📎 {file.name}</span>
                      <button
                        onClick={() => setUploadedFiles(uploadedFiles.filter((_, i) => i !== index))}
                        className="hover:text-[#E85D4D]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.docx,.txt,.png,.jpg,.jpeg"
                  multiple
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="retro-button-small bg-[#6B4423]"
                  title="Upload file"
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onPaste={handlePaste}
                  placeholder="Type your message... (Ctrl+V to paste images)"
                  className="flex-1 px-4 py-3 bg-[#6B4423] text-[#F4E4C1] rounded resize-none border-2 border-[#E8934D] focus:outline-none focus:border-[#E85D4D]"
                  rows={3}
                  disabled={loading}
                />

                {loading ? (
                  <button
                    onClick={stopGeneration}
                    className="retro-button-small bg-red-600 hover:bg-red-700 flex items-center gap-2"
                    data-testid="stop-generation-btn"
                    title="Stop generating"
                  >
                    <Square className="w-4 h-4 fill-white" />
                    <span className="text-sm">Stop</span>
                  </button>
                ) : (
                  <button
                    onClick={sendMessage}
                    disabled={!inputText.trim() && uploadedFiles.length === 0}
                    className="retro-button-small bg-[#E8934D] hover:bg-[#E85D4D] disabled:opacity-50"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              <div className="mt-2 text-xs text-[#E8B67A] text-center">
                Tip: Paste screenshots with Ctrl+V • Upload PDFs, DOCX, TXT, images • Use Test Mode for exam questions
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upgrade Modal */}
      {showUpgradeModal && proSettings && (
        <ErnieProUpgradeModal 
          onClose={() => setShowUpgradeModal(false)}
          monthlyPrice={proSettings.monthly_price}
        />
      )}
    </div>
  );
}
